import { Badge, Button } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, DownloadIcon, EditIcon, PlusIcon, Trash2Icon } from "lucide-react";
import type { JDDetailDTO } from "@/modules/jd/domain/contracts";
import { useRemoveJdKpi } from "../api/queries";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { JDKpiAddModal } from "./JDKpiAddModal";
import { JDVersionHistory } from "./JDVersionHistory";

type JDDetailViewProps = {
  jd: JDDetailDTO;
  onEdit?: () => void;
  onDownloadPDF?: () => void;
  canEdit?: boolean;
  hideVersionHistory?: boolean;
  hideHeader?: boolean;
};

const statusLabels: Record<string, string> = {
  draft: "ຮ່າງ",
  published: "ໃຊ້ງານ",
  archived: "ບໍ່ໃຊ້ງານ",
};

const statusVariants: Record<string, "default" | "secondary" | "destructive"> =
  {
    draft: "secondary",
    published: "default",
    archived: "destructive",
  };

const frequencyLabels: Record<string, string> = {
  daily: "ປະຈຳວັນ",
  weekly: "ປະຈຳອາທິດ",
  monthly: "ປະຈຳເດືອນ",
  quarterly: "ປະຈຳໄຕມາດ",
  annual: "ປະຈຳປີ",
};

const requirementTypeLabels: Record<string, string> = {
  experience: "ປະສົບການ",
  skills: "ທັກສະ",
  knowledge: "ຄວາມຮູ້",
  education: "ການສຶກສາ",
  other: "ອື່ນໆ",
};

function JDKpiSection({
  jd,
  canEdit,
}: {
  jd: JDDetailDTO;
  canEdit: boolean;
}) {
  const nav = useNavigate();
  const addModal = useDisclosure(false);
  const removeKpi = useRemoveJdKpi(jd.id);
  const kpis = jd.kpis ?? [];

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-lg">ກໍານົດ KPI ຂອງຕໍາແໜ່ງ</h2>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={addModal.open}>
            <PlusIcon className="mr-1 size-4" />
            ເພີ່ມ KPI
          </Button>
        )}
      </div>
      {kpis.length > 0 ? (
        <ul className="space-y-2">
          {kpis.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm"
            >
              <button
                type="button"
                className="flex-1 text-left hover:underline"
                onClick={() => nav({ to: `/app/kpi/${item.kpiId}` })}
              >
                {item.kpiName ?? item.kpiId}
              </button>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive hover:bg-destructive/10"
                  onClick={() => removeKpi.mutate(item.kpiId)}
                  disabled={removeKpi.isPending}
                >
                  <Trash2Icon className="size-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-sm">
          ບໍ່ມີ KPI ທີ່ກຳນົດສຳລັບຕໍາແໜ່ງນີ້ — ກົດ ເພີ່ມ KPI ເພື່ອໃຫ້ຕໍາແໜ່ງນີ້ມີບັນຊີ KPI ຕາມ JD
        </p>
      )}
      <JDKpiAddModal
        jdId={jd.id}
        open={addModal.isOpen}
        onOpenChange={(v) => {
          if (!v) addModal.close();
        }}
        excludedKpiIds={kpis.map((k) => k.kpiId)}
        onSuccess={addModal.close}
      />
    </section>
  );
}

export function JDDetailView({
  jd,
  onEdit,
  onDownloadPDF,
  canEdit,
  hideVersionHistory,
  hideHeader,
}: JDDetailViewProps) {
  const nav = useNavigate({ from: "/app/jd/$id" });
  return (
    <div className="space-y-6">
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-3xl">{jd.title}</h1>
              <Badge variant={statusVariants[jd.status] || "secondary"}>
                {statusLabels[jd.status] || jd.status}
              </Badge>
              <Badge variant="outline">{jd.level || "ບໍ່ມີລະດັບ"}</Badge>
            </div>
            <p className="mt-2 text-muted-foreground">
              {jd.departmentName || "ບໍ່ມີພະແນກ"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => nav({ to: "/app/jd" })}>
              <ArrowLeftIcon className="size-4" />
              ກັບໄປລາຍການ
            </Button>
            {onDownloadPDF && (
              <Button variant="outline" onClick={onDownloadPDF}>
                <DownloadIcon className="h-4 w-4" />
                ເບິ່ງ PDF Preview
              </Button>
            )}
            {canEdit && onEdit && (
              <Button onClick={onEdit}>
                <EditIcon className="h-4 w-4" />
                ແກ້ໄຂ
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Section 1: Position Info */}
      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="font-semibold text-lg">ຂໍ້ມູນຕໍາແໜ່ງ</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">ພະແນກ:</span>
            <p className="font-medium">{jd.departmentName || "-"}</p>
          </div>
          <div>
            <span className="text-muted-foreground">ລະດັບ:</span>
            <p className="font-medium">{jd.level || "ບໍ່ມີລະດັບ"}</p>
          </div>
          {jd.reportsToPositionTitle && (
            <div>
              <span className="text-muted-foreground">ລາຍງານໄປຫາ:</span>
              <p className="font-medium">{jd.reportsToPositionTitle}</p>
            </div>
          )}
          {jd.managesCount && (
            <div>
              <span className="text-muted-foreground">ດູແລຈຳນວນຄົນ:</span>
              <p className="font-medium">{jd.managesCount} ຄົນ</p>
            </div>
          )}
        </div>
      </section>

      {/* Section 2: Main Responsibilities */}
      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="font-semibold text-lg">ຫນ້າທີ່ຫຼັກ</h2>
        {jd.responsibilities && jd.responsibilities.length > 0 ? (
          <ul className="space-y-2">
            {jd.responsibilities.map((resp) => (
              <li key={resp.id} className="flex items-start gap-2">
                <span className="mt-1 text-primary">•</span>
                <div
                  className="prose prose-sm max-w-none flex-1"
                  dangerouslySetInnerHTML={{ __html: resp.description }}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>

      {/* Section 3: Routine Tasks */}
      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="font-semibold text-lg">ວຽກປະຈຳ</h2>
        {jd.routineTasks && jd.routineTasks.length > 0 ? (
          <div className="space-y-4">
            {["daily", "weekly", "monthly", "quarterly", "annual"].map(
              (freq) => {
                const tasks = jd.routineTasks.filter(
                  (t) => t.frequency === freq,
                );
                if (tasks.length === 0) return null;
                return (
                  <div key={freq}>
                    <h3 className="mb-2 font-medium text-muted-foreground text-sm">
                      {frequencyLabels[freq]}
                    </h3>
                    <ul className="space-y-1">
                      {tasks.map((task) => (
                        <li
                          key={task.id}
                          className="flex items-start gap-2 text-sm"
                        >
                          <span className="mt-1 text-primary">•</span>
                          <span>{task.description}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              },
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>

      {/* Section 4: Requirements */}
      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="font-semibold text-lg">ຄຸນສົມບັດທີ່ຕ້ອງການ</h2>
        {jd.requirements && jd.requirements.length > 0 ? (
          <div className="space-y-4">
            {["experience", "skills", "knowledge", "education", "other"].map(
              (type) => {
                const reqs = jd.requirements.filter((r) => r.type === type);
                if (reqs.length === 0) return null;
                return (
                  <div key={type}>
                    <h3 className="mb-2 font-medium text-sm">
                      {requirementTypeLabels[type]}
                    </h3>
                    <ul className="space-y-1">
                      {reqs.map((req) => (
                        <li
                          key={req.id}
                          className="flex items-start gap-2 text-sm"
                        >
                          <span className="mt-1 text-primary">•</span>
                          <div className="flex-1">
                            <span>{req.description}</span>
                            {req.isRequired && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                ຈຳເປັນ
                              </Badge>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              },
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
        )}
      </section>

      {/* Section 5: KPI ของตำแหน่ง */}
      <JDKpiSection jd={jd} canEdit={!!canEdit} />

      {/* Section 6: Version History */}
      {!hideVersionHistory && (
        <section className="space-y-3 rounded-lg border bg-card p-4">
          <JDVersionHistory jdId={jd.id} />
        </section>
      )}
    </div>
  );
}
